import React, { useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  TrophyIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ChartBarIcon,
  LightBulbIcon,
  StarIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'
import { StudentPageLayout } from '../../components/student/StudentPageLayout'
import { StudentSection } from '../../components/student/StudentSection'

const ExamResult: React.FC = () => {
  const { examId } = useParams<{ examId: string }>()
  const navigate = useNavigate()

  const [examResult] = useState({
    examTitle: 'JavaScript 基础知识测试',
    score: 85,
    totalScore: 100,
    percentage: 85,
    passed: true,
    timeUsed: 25,
    correctAnswers: 17,
    totalQuestions: 20,
    feedback: '表现良好！你对 JavaScript 基础语法掌握扎实，建议继续巩固异步与模块化内容。',
    teacherComment: '建议在真实项目中加强练习，保持刷题节奏，巩固高频考点。',
    rank: 8,
    totalParticipants: 45
  })

  const [analysis] = useState({
    correctByTopic: {
      数据类型: { correct: 4, total: 5 },
      变量声明: { correct: 3, total: 4 },
      函数: { correct: 4, total: 4 },
      数组操作: { correct: 3, total: 4 },
      对象: { correct: 3, total: 3 }
    },
    wrongQuestions: [
      {
        id: 3,
        question: '以下哪个不是 JavaScript 的数据类型？',
        yourAnswer: 'float',
        correctAnswer: 'undefined',
        explanation: 'JavaScript 没有 float 类型，数字统一为 number。'
      },
      {
        id: 8,
        question: '如何在数组开头添加元素？',
        yourAnswer: 'push()',
        correctAnswer: 'unshift()',
        explanation: 'push 在末尾添加，unshift 在开头添加。'
      }
    ]
  })

  const layoutMetrics = useMemo(
    () => [
      {
        label: '总分',
        value: `${examResult.score}/${examResult.totalScore}`,
        helper: `得分率 ${examResult.percentage}%`
      },
      {
        label: '正确题数',
        value: `${examResult.correctAnswers}/${examResult.totalQuestions}`,
        helper: '知识点覆盖度 92%'
      },
      {
        label: '耗时',
        value: `${examResult.timeUsed} 分钟`,
        helper: '高于平均速度'
      },
      {
        label: '排名',
        value: `#${examResult.rank}`,
        helper: `共 ${examResult.totalParticipants} 人`
      }
    ],
    [examResult]
  )

  const heroExtra = (
    <div className="space-y-3 sm:space-y-4 text-white">
      <p className="text-xs sm:text-sm text-white/80">AI 自动生成学习建议，并可同步到下一次考试的训练计划。</p>
      <div className="grid grid-cols-2 gap-2 sm:gap-4">
        <div className="bg-white/10 rounded-xl sm:rounded-2xl border border-white/20 p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-white/70">当前评级</p>
          <p className="text-xl sm:text-2xl font-semibold mt-0.5 sm:mt-1">{examResult.percentage >= 85 ? 'A-' : 'B+'}</p>
          <p className="text-[10px] sm:text-xs text-white/70 mt-0.5 sm:mt-1">保持稳定发挥</p>
        </div>
        <div className="bg-white/10 rounded-xl sm:rounded-2xl border border-white/20 p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-white/70">下一步建议</p>
          <p className="text-xl sm:text-2xl font-semibold mt-0.5 sm:mt-1 truncate">巩固异步</p>
          <p className="text-[10px] sm:text-xs text-white/70 mt-0.5 sm:mt-1 truncate">AI 将推送专项练习</p>
        </div>
      </div>
    </div>
  )

  const scoreBadge = examResult.passed ? (
    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800">
      <CheckCircleIcon className="w-4 h-4 mr-2" />
      考试通过
    </span>
  ) : (
    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800">
      <XCircleIcon className="w-4 h-4 mr-2" />
      继续加油
    </span>
  )

  return (
    <StudentPageLayout
      title="考试结果"
      description="回顾本次考试表现，获取 AI 分析、老师点评与错题清单，持续优化备考策略。"
      badge="智能评测完成"
      metrics={layoutMetrics}
      heroExtra={heroExtra}
      actions={
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <button className="px-4 sm:px-5 py-2.5 text-sm font-medium text-white/90 border border-white/40 rounded-xl sm:rounded-2xl hover:bg-white/10 transition flex items-center justify-center">
            <SparklesIcon className="w-4 h-4 mr-2" />
            生成复习计划
          </button>
          <Link
            to={`/student/exams/${examId}/take`}
            className="px-4 sm:px-5 py-2.5 text-sm font-medium text-blue-900 bg-white rounded-xl sm:rounded-2xl hover:bg-blue-50 transition text-center"
          >
            重新挑战
          </Link>
        </div>
      }
    >
      {/* 面包屑导航 */}
      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6 overflow-x-auto pb-1">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 flex-shrink-0">
          <ArrowLeftIcon className="w-4 sm:w-5 h-4 sm:h-5 mr-1" />
          <span className="hidden sm:inline">返回</span>
        </button>
        <span className="text-gray-400 flex-shrink-0">/</span>
        <Link to="/student/exams" className="hover:text-gray-700 flex-shrink-0">
          考试中心
        </Link>
        <span className="text-gray-400 flex-shrink-0">/</span>
        <span className="text-gray-700 flex-shrink-0">考试结果</span>
      </div>

      <StudentSection title="成绩概览" description="整体表现与通过情况" icon={TrophyIcon}>
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-gray-100 shadow-sm p-4 sm:p-6">
          {/* 标题和状态 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div
                className={`w-14 h-14 sm:w-20 sm:h-20 rounded-full flex items-center justify-center flex-shrink-0 ${
                  examResult.passed ? 'bg-green-100' : 'bg-red-100'
                }`}
              >
                {examResult.passed ? (
                  <TrophyIcon className="w-7 h-7 sm:w-10 sm:h-10 text-green-600" />
                ) : (
                  <XCircleIcon className="w-7 h-7 sm:w-10 sm:h-10 text-red-600" />
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 line-clamp-1">{examResult.examTitle}</h1>
                <p className="text-xs sm:text-base text-gray-600 mt-0.5 sm:mt-1">AI 已完成自动评分与知识点归纳</p>
              </div>
            </div>
            {scoreBadge}
          </div>

          {/* 成绩统计卡片 - 2x2网格，桌面端4列 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
            <div className="text-center p-3 sm:p-4 bg-blue-50 rounded-xl sm:rounded-2xl">
              <DocumentTextIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mx-auto mb-1 sm:mb-2" />
              <div className="text-2xl sm:text-3xl font-bold text-blue-900">{examResult.score}</div>
              <p className="text-xs sm:text-sm text-blue-600">总分 / {examResult.totalScore}</p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-green-50 rounded-xl sm:rounded-2xl">
              <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 mx-auto mb-1 sm:mb-2" />
              <div className="text-2xl sm:text-3xl font-bold text-green-900">{examResult.timeUsed}′</div>
              <p className="text-xs sm:text-sm text-green-600">用时</p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-purple-50 rounded-xl sm:rounded-2xl">
              <ChartBarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 mx-auto mb-1 sm:mb-2" />
              <div className="text-2xl sm:text-3xl font-bold text-purple-900">
                {examResult.correctAnswers}/{examResult.totalQuestions}
              </div>
              <p className="text-xs sm:text-sm text-purple-600">正确题数</p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-yellow-50 rounded-xl sm:rounded-2xl">
              <StarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 mx-auto mb-1 sm:mb-2" />
              <div className="text-2xl sm:text-3xl font-bold text-yellow-900">#{examResult.rank}</div>
              <p className="text-xs sm:text-sm text-yellow-600">共 {examResult.totalParticipants} 人</p>
            </div>
          </div>
        </div>
      </StudentSection>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <StudentSection title="AI 智能分析" description="行为洞察与学习建议" icon={LightBulbIcon}>
          <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 space-y-3 sm:space-y-4">
            <p className="text-sm sm:text-base text-blue-900 leading-relaxed">{examResult.feedback}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm text-blue-700">
              <div className="bg-white/70 rounded-xl sm:rounded-2xl border border-blue-100 p-3">
                <p className="font-semibold text-blue-900 text-sm">优势模块</p>
                <p className="text-xs sm:text-sm mt-1">函数、对象题型全对，思路清晰。</p>
              </div>
              <div className="bg-white/70 rounded-xl sm:rounded-2xl border border-blue-100 p-3">
                <p className="font-semibold text-blue-900 text-sm">强化建议</p>
                <p className="text-xs sm:text-sm mt-1">增加异步编程练习，降低错题率。</p>
              </div>
            </div>
          </div>
        </StudentSection>

        <StudentSection title="老师点评" description="教研团队的个性化建议" icon={CheckCircleIcon}>
          <div className="bg-gradient-to-br from-green-50 to-white border border-green-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 space-y-3 sm:space-y-4">
            <p className="text-sm sm:text-base text-green-900 leading-relaxed">{examResult.teacherComment}</p>
            <ul className="text-xs sm:text-sm text-green-700 space-y-1.5 sm:space-y-2">
              <li>• 维持日常刷题频率，保持手感</li>
              <li>• 结合项目实践巩固概念应用</li>
              <li>• 审题阶段加入 10 秒复核步骤</li>
            </ul>
          </div>
        </StudentSection>
      </div>

      <StudentSection title="知识点掌握" description="题型准确率与薄弱点" icon={DocumentTextIcon}>
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 space-y-6">
          {Object.entries(analysis.correctByTopic).map(([topic, data]) => {
            const percentage = Math.round((data.correct / data.total) * 100)
            const barColor =
              percentage >= 80 ? 'bg-green-500' : percentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'
            return (
              <div key={topic}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-900">{topic}</span>
                  <span className="text-sm text-gray-600">
                    {data.correct}/{data.total}（{percentage}%）
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div className={`h-2 rounded-full ${barColor}`} style={{ width: `${percentage}%` }} />
                </div>
              </div>
            )
          })}
        </div>
      </StudentSection>

      <StudentSection title="错题解析" description="快速复盘，建立错题本" icon={XCircleIcon}>
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
          {analysis.wrongQuestions.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircleIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">全部答对，太棒了！</h4>
              <p className="text-gray-500">系统将自动生成巩固练习，帮助你巩固优势。</p>
            </div>
          ) : (
            <div className="space-y-6">
              {analysis.wrongQuestions.map((question) => (
                <div key={question.id} className="border border-red-200 rounded-3xl p-5 bg-red-50/40">
                  <div className="flex items-start mb-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3">
                      {question.id}
                    </span>
                    <h4 className="font-semibold text-gray-900">{question.question}</h4>
                  </div>
                  <div className="ml-11 space-y-2 text-sm">
                    <div className="flex flex-wrap gap-3">
                      <span className="text-gray-600">你的答案：</span>
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded">{question.yourAnswer}</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <span className="text-gray-600">正确答案：</span>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded">{question.correctAnswer}</span>
                    </div>
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-2xl text-blue-800">
                      <p className="font-medium mb-1">解析</p>
                      <p>{question.explanation}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </StudentSection>

      {/* 底部操作按钮 - 移动端全宽堆叠 */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
        <Link
          to="/student/exams"
          className="flex items-center justify-center px-5 py-3 sm:py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          返回考试中心
        </Link>
        <Link
          to={`/student/exams/${examId}`}
          className="flex items-center justify-center px-5 py-3 sm:py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <DocumentTextIcon className="w-5 h-5 mr-2" />
          查看考试详情
        </Link>
        <button className="flex items-center justify-center px-5 py-3 sm:py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-medium">
          <SparklesIcon className="w-5 h-5 mr-2" />
          同步 AI 训练
        </button>
      </div>
    </StudentPageLayout>
  )
}

export default ExamResult

